
import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Search,
  Filter,
  Target,
  Users,
  DollarSign,
  Settings,
  Calendar,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  RefreshCw,
  CheckCircle,
  ArrowDown,
  ArrowRight,
  Eye,
  Lightbulb,
  Layers,
  ArrowLeft,
  Gauge,
  Scale
} from "lucide-react";
import { useStrategicMap } from "../hooks/useStrategicMap";
import { authenticatedFetch } from "../services/auth";
import { API_BASE } from "../utils/apiConfig";

const StrategicFlow = ({ closeStrategicFlow }) => {
  const { strategicMapData, loading, error } = useStrategicMap();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPerspectives, setSelectedPerspectives] = useState([
    "Financeiro",
    "Clientes",
    "Processos",
    "Pessoas",
  ]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [globalPerf, setGlobalPerf] = useState(null);

  // Busca a performance global ponderada do período selecionado (YYYY-MM)
  useEffect(() => {
    if (!selectedPeriod || !/^\d{4}-\d{2}$/.test(selectedPeriod)) {
      setGlobalPerf(null);
      return;
    }
    const [yy, mm] = selectedPeriod.split("-").map(Number);
    const lastDay = new Date(yy, mm, 0).getDate();
    const endDate = `${yy}-${String(mm).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    const prev = new Date(yy, mm - 2, 1); // 1º dia do mês anterior (p/ tendência)
    const startDate = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}-01`;

    let cancelled = false;
    (async () => {
      try {
        const resp = await authenticatedFetch(
          `${API_BASE}/api/bsc/performance?start_date=${startDate}&end_date=${endDate}`,
        );
        if (!cancelled && resp.ok) {
          setGlobalPerf(await resp.json());
        } else if (!cancelled) {
          setGlobalPerf(null);
        }
      } catch (e) {
        if (!cancelled) setGlobalPerf(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedPeriod]);

  // Cores por faixa de performance
  const perfText = (v) =>
    v >= 100 ? "text-green-600" : v >= 80 ? "text-emerald-600" : v >= 50 ? "text-amber-600" : "text-red-600";
  const perfBg = (v) =>
    v >= 100 ? "bg-green-500" : v >= 80 ? "bg-emerald-500" : v >= 50 ? "bg-amber-500" : "bg-red-500";

  const openObjectiveDetail = (objective, perspective) => {
    setSelectedObjective({ ...objective, perspective });
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedObjective(null);
  };

  // Extrair períodos únicos dos dados
  const availablePeriods = [...new Set(strategicMapData.map(p => p.period))].filter(Boolean);

  // Carregar automaticamente o último período disponível
  useEffect(() => {
    if (availablePeriods.length > 0 && !selectedPeriod) {
      const sortedPeriods = availablePeriods.sort((a, b) => b.localeCompare(a));
      const latestPeriod = sortedPeriods[0]; // Último período (mais recente)
      setSelectedPeriod(latestPeriod);
    }
  }, [availablePeriods, selectedPeriod]);

  const getPerspectiveIcon = (perspective) => {
    switch (perspective) {
      case "Financeiro":
        return <DollarSign className="w-4 h-4" />;
      case "Clientes":
        return <Users className="w-4 h-4" />;
      case "Processos":
        return <Settings className="w-4 h-4" />;
      case "Pessoas":
        return <Target className="w-4 h-4" />;
      default:
        return <Target className="w-4 h-4" />;
    }
  };

  const getPerspectiveColor = (perspective) => {
    switch (perspective) {
      case "Financeiro":
        return "bg-emerald-600";
      case "Clientes":
        return "bg-blue-600";
      case "Processos":
        return "bg-purple-600";
      case "Pessoas":
        return "bg-orange-600";
      default:
        return "bg-gray-600";
    }
  };

  const getPerformanceColor = (percentAchieved) => {
    if (percentAchieved >= 100) return "bg-green-500";
    if (percentAchieved >= 90) return "bg-blue-500";
    if (percentAchieved >= 70) return "bg-yellow-500";
    return "bg-red-500";
  };

  const getPerformanceIcon = (percentAchieved) => {
    if (percentAchieved >= 100) return <TrendingUp className="w-4 h-4" />;
    if (percentAchieved > 95) return <TrendingUp className="w-4 h-4" />;
    if (percentAchieved < 95) return <AlertCircle className="w-4 h-4" />;
    return <TrendingDown className="w-4 h-4" />;
  };

  const togglePerspective = (perspective) => {
    setSelectedPerspectives((prev) =>
      prev.includes(perspective)
        ? prev.filter((p) => p !== perspective)
        : [...prev, perspective],
    );
  };

  const filteredData = strategicMapData.filter((perspectiveData) => {
    const matchesPerspective = selectedPerspectives.includes(
      perspectiveData.perspective,
    );

    const matchesPeriod = perspectiveData.period === selectedPeriod;

    if (!searchTerm.trim()) return matchesPerspective && matchesPeriod;

    const term = searchTerm.toLowerCase();
    const matchesSearch = perspectiveData.objectives.some(
      (objective) =>
        objective.objective.toLowerCase().includes(term),
    );

    return matchesPerspective && matchesPeriod && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mb-4"></div>
          <p className="text-gray-600 font-medium">Carregando Fluxo Estratégico...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 text-lg font-medium mb-4">{error}</p>
          <button
            onClick={closeStrategicFlow}
            className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header do Fluxo Estratégico */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">

            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Target className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Fluxo Estratégico BSC</h1>
                <p className="text-gray-600 text-sm">Conexões entre Objetivos Estratégicos</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">



          </div>
        </div>
      </div>

      <div className="max-w-8xl mx-auto p-6">
        {/* Filtros */}
        <div className="bg-white rounded-xl p-5 shadow-lg mb-5">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filtros e Busca
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Busca */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buscar Objetivos
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Digite para buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>

            {/* Filtro de Período */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Período
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 appearance-none bg-white"
                >
                  {availablePeriods.sort((a, b) => b.localeCompare(a)).map((period) => (
                    <option key={period} value={period}>
                      {period}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Filtros de Perspectiva */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Perspectivas
              </label>
              <div className="flex flex-wrap gap-2">
                {["Financeiro", "Clientes", "Processos", "Pessoas"].map(
                  (perspective) => (
                    <button
                      key={perspective}
                      onClick={() => togglePerspective(perspective)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all border-2 ${selectedPerspectives.includes(perspective)
                        ? `${getPerspectiveColor(perspective)} text-white border-transparent`
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                        }`}
                    >
                      {getPerspectiveIcon(perspective)}
                      {perspective}
                      {selectedPerspectives.includes(perspective) && (
                        <CheckCircle className="w-3 h-3" />
                      )}
                    </button>
                  ),
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Fluxo Estratégico */}
        <div className="bg-white rounded-xl shadow-lg">
          {/* Cabeçalho principal do diagrama com a Performance Global do período */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div
                  className={`p-3 rounded-xl shadow-lg ${
                    globalPerf && typeof globalPerf.performance === "number"
                      ? perfBg(globalPerf.performance)
                      : "bg-purple-500"
                  }`}
                >
                  <Gauge className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Diagrama de Fluxo Estratégico
                  </h3>
                  {globalPerf && typeof globalPerf.performance === "number" ? (
                    <div className="flex items-end gap-3 mt-1 flex-wrap">
                      <span className={`text-4xl font-bold leading-none ${perfText(globalPerf.performance)}`}>
                        {globalPerf.performance.toFixed(1)}%
                      </span>
                      <span className="text-sm text-gray-500 mb-1">
                        Performance global{globalPerf.periodLabel ? ` — ${globalPerf.periodLabel}` : ""}
                      </span>
                      {globalPerf.previous != null && (
                        <span
                          className={`text-sm font-medium flex items-center gap-1 mb-1 ${
                            globalPerf.trend === "up"
                              ? "text-green-600"
                              : globalPerf.trend === "down"
                                ? "text-red-600"
                                : "text-gray-500"
                          }`}
                        >
                          {globalPerf.trend === "up" ? (
                            <TrendingUp className="w-4 h-4" />
                          ) : globalPerf.trend === "down" ? (
                            <TrendingDown className="w-4 h-4" />
                          ) : null}
                          {(globalPerf.performance - globalPerf.previous).toFixed(1)} p.p.
                        </span>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-600 mt-1">
                      Visualização das conexões entre objetivos estratégicos por perspectiva
                    </p>
                  )}
                </div>
              </div>

              {globalPerf && Array.isArray(globalPerf.perspectives) && globalPerf.perspectives.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {globalPerf.perspectives.map((p) => (
                    <div
                      key={p.perspective}
                      className="px-3.5 py-2 rounded-md bg-gray-50 border border-gray-200 min-w-[105px] leading-tight"
                    >
                      <p className="text-sm text-gray-500 truncate">{p.perspective}</p>
                      <p className={`text-xl font-bold ${perfText(p.performance)}`}>
                        {p.performance.toFixed(1)}%
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="p-6">
            <StrategicFlowDiagram
              filteredData={filteredData}
              getPerspectiveIcon={getPerspectiveIcon}
              getPerspectiveColor={getPerspectiveColor}
              getPerformanceColor={getPerformanceColor}
              searchTerm={searchTerm}
              openObjectiveDetail={openObjectiveDetail}
            />
          </div>
        </div>

        {filteredData.length === 0 && (
          <div className="bg-white rounded-xl p-12 text-center shadow-lg">
            <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-3">
              Nenhuma perspectiva encontrada
            </h3>
            <p className="text-gray-500">
              Ajuste os filtros ou termos de busca para visualizar o fluxo estratégico.
            </p>
          </div>
        )}

        {/* Modal de Detalhes do Objetivo */}
        {showDetailModal && selectedObjective && (
          <ObjectiveDetailModal
            objective={selectedObjective}
            closeModal={closeDetailModal}
            getPerformanceColor={getPerformanceColor}
            getPerformanceIcon={getPerformanceIcon}
            searchTerm={searchTerm}
          />
        )}
      </div>
    </div>
  );
};

const StrategicFlowDiagram = ({
  filteredData,
  getPerspectiveIcon,
  getPerspectiveColor,
  getPerformanceColor,
  searchTerm,
  openObjectiveDetail,
}) => {
  const containerRef = useRef(null);
  const [connections, setConnections] = useState([]);

  const highlightText = React.useCallback((text, term) => {
    if (!term.trim()) return text;
    const regex = new RegExp(`(${term})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 px-1 rounded">
          {part}
        </mark>
      ) : (
        part
      ),
    );
  }, []);

  // Memoizar os dados ordenados para evitar recriação desnecessária
  const sortedData = React.useMemo(() => {
    const perspectiveOrder = ["Financeiro", "Clientes", "Processos", "Pessoas"];
    return [...filteredData].sort((a, b) =>
      perspectiveOrder.indexOf(a.perspective) - perspectiveOrder.indexOf(b.perspective)
    );
  }, [filteredData]);

  // Memoizar mapa de objetivos para evitar recriação desnecessária
  const allObjectives = React.useMemo(() => {
    const objectives = {};
    sortedData.forEach(perspectiveData => {
      perspectiveData.objectives.forEach(objective => {
        objectives[objective.objectiveId] = {
          ...objective,
          perspective: perspectiveData.perspective
        };
      });
    });
    return objectives;
  }, [sortedData]);

  // Calcular posições das conexões
  useEffect(() => {
    const calculateConnections = () => {
      if (!containerRef.current) return;

      const newConnections = [];

      sortedData.forEach(perspectiveData => {
        perspectiveData.objectives.forEach(objective => {
          if (objective.connections && objective.connections.length > 0) {
            objective.connections.forEach(targetId => {
              const sourceElement = document.getElementById(`objective-${objective.objectiveId}`);
              const targetElement = document.getElementById(`objective-${targetId}`);

              if (sourceElement && targetElement) {
                const containerRect = containerRef.current.getBoundingClientRect();
                const sourceRect = sourceElement.getBoundingClientRect();
                const targetRect = targetElement.getBoundingClientRect();

                const connection = {
                  id: `${objective.objectiveId}-${targetId}`,
                  startX: sourceRect.left + sourceRect.width / 2 - containerRect.left,
                  startY: sourceRect.top + sourceRect.height / 2 - containerRect.top,
                  endX: targetRect.left + targetRect.width / 2 - containerRect.left,
                  endY: targetRect.top + targetRect.height / 2 - containerRect.top,
                  sourceObjective: objective,
                  targetObjective: allObjectives[targetId]
                };

                newConnections.push(connection);
              }
            });
          }
        });
      });

      setConnections(newConnections);
    };

    // Usar um pequeno delay para garantir que os elementos estejam renderizados
    const timeoutId = setTimeout(calculateConnections, 100);

    return () => clearTimeout(timeoutId);
  }, [sortedData, allObjectives]);

  return (
    <div className="relative" ref={containerRef}>
      {/* SVG para as conexões */}
      <svg
        className="absolute inset-0 pointer-events-none z-10"
        style={{ width: '100%', height: '100%' }}
      >
        <defs>
          {/* Gradiente simples para o fluxo */}
          <linearGradient id="flowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.4" />
            <stop offset="50%" stopColor="#8B5CF6" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.4" />
          </linearGradient>

          {/* Padrão simples de pontos para o fluxo */}
          <pattern id="flowPattern" x="0" y="0" width="20" height="4" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="#8B5CF6" opacity="0.6">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0,0; 20,0; 0,0"
                dur="2s"
                repeatCount="indefinite"
              />
            </circle>
          </pattern>
        </defs>

        {connections.map((connection, index) => {
          const angle = Math.atan2(
            connection.endY - connection.startY,
            connection.endX - connection.startX
          ) * 180 / Math.PI;

          return (
            <g key={connection.id}>
              {/* Linha base clara */}
              <line
                x1={connection.startX}
                y1={connection.startY}
                x2={connection.endX}
                y2={connection.endY}
                stroke="#E5E7EB"
                strokeWidth="2"
              />

              {/* Linha principal com fluxo constante */}
              <line
                x1={connection.startX}
                y1={connection.startY}
                x2={connection.endX}
                y2={connection.endY}
                stroke="#8B5CF6"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray="10,5"
                opacity="0.7"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  values="0;-15"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </line>

              {/* Partícula simples que se move */}
              <circle
                r="3"
                fill="#8B5CF6"
                opacity="0.8"
              >
                <animateMotion
                  dur="3s"
                  begin={`${index * 0.5}s`}
                  repeatCount="indefinite"
                >
                  <mpath href={`#path-${connection.id}`} />
                </animateMotion>
              </circle>

              {/* Caminho para animação */}
              <path
                id={`path-${connection.id}`}
                d={`M ${connection.startX} ${connection.startY} L ${connection.endX} ${connection.endY}`}
                fill="none"
                stroke="none"
              />

              {/* Seta simples no final */}
              <polygon
                points={`${connection.endX - 8},${connection.endY - 4} ${connection.endX + 8},${connection.endY} ${connection.endX - 8},${connection.endY + 4}`}
                fill="#8B5CF6"
                transform={`rotate(${angle}, ${connection.endX}, ${connection.endY})`}
                opacity="0.8"
              />
            </g>
          );
        })}
      </svg>

      {/* Grid de Perspectivas */}
      <div className="space-y-8 relative z-20">
        {sortedData.map((perspectiveData, perspectiveIndex) => (
          <div key={`${perspectiveData.perspective}-${perspectiveData.period}`} className="relative">
            {/* Cabeçalho da Perspectiva (compacto) */}
            <div className={`${getPerspectiveColor(perspectiveData.perspective)} text-white px-4 py-2.5 rounded-lg mb-4 shadow-md`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  {getPerspectiveIcon(perspectiveData.perspective)}
                  <h2 className="text-lg font-bold truncate">{perspectiveData.perspective}</h2>
                  <span className="bg-white/20 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap">
                    {perspectiveData.objectives.length} obj.
                  </span>
                  {perspectiveData.weight != null && (
                    <span
                      className="bg-white/20 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
                      title="Peso da perspectiva no cálculo da performance global"
                    >
                      Peso {parseFloat(perspectiveData.weight)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  {perspectiveData.period && (
                    <span className="text-xs opacity-80 whitespace-nowrap hidden sm:inline">
                      {perspectiveData.period}
                    </span>
                  )}
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-2xl font-bold leading-none">{perspectiveData.average}%</span>
                    <span className="text-[11px] opacity-75">perf.</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Grid de Objetivos — cards compactos, mais por linha */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
              {perspectiveData.objectives.map((objective, objectiveIndex) => (
                <CompactObjectiveCard
                  key={objective.objectiveId}
                  objective={objective}
                  perspective={perspectiveData.perspective}
                  getPerformanceColor={getPerformanceColor}
                  highlightText={highlightText}
                  searchTerm={searchTerm}
                  allObjectives={allObjectives}
                  openObjectiveDetail={openObjectiveDetail}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CompactObjectiveCard = ({
  objective,
  perspective,
  getPerformanceColor,
  highlightText,
  searchTerm,
  allObjectives,
  openObjectiveDetail,
}) => {
  const getPerspectiveGradient = (perspective) => {
    switch (perspective) {
      case "Financeiro":
        return "from-emerald-50 to-emerald-100 border-emerald-300 hover:border-emerald-500";
      case "Clientes":
        return "from-blue-50 to-blue-100 border-blue-300 hover:border-blue-500";
      case "Processos":
        return "from-purple-50 to-purple-100 border-purple-300 hover:border-purple-500";
      case "Pessoas":
        return "from-orange-50 to-orange-100 border-orange-300 hover:border-orange-500";
      default:
        return "from-gray-50 to-gray-100 border-gray-300 hover:border-gray-500";
    }
  };

  return (
    <div
      id={`objective-${objective.objectiveId}`}
      className={`bg-gradient-to-br ${getPerspectiveGradient(perspective)} border rounded-lg p-3 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 cursor-pointer`}
      onClick={() => openObjectiveDetail(objective, perspective)}
    >
      {/* Nome + nº de conexões */}
      <div className="flex items-start justify-between gap-2 mb-2 min-h-[2.4rem]">
        <h3 className="font-semibold text-gray-800 text-xs leading-snug line-clamp-2">
          {highlightText(objective.objective, searchTerm)}
        </h3>
        {objective.connections && objective.connections.length > 0 && (
          <span
            className="shrink-0 bg-purple-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold"
            title={`${objective.connections.length} conexão(ões)`}
          >
            {objective.connections.length}
          </span>
        )}
      </div>

      {/* Performance + Peso na mesma linha */}
      <div className="flex items-center justify-between gap-2">
        <span className={`${getPerformanceColor(objective.average)} text-white rounded-md px-2 py-1 text-sm font-bold leading-none`}>
          {objective.average}%
        </span>
        {objective.weight != null && (
          <span
            className="inline-flex items-center rounded bg-white/70 px-1.5 py-0.5 text-[11px] font-semibold text-gray-700 ring-1 ring-gray-300"
            title="Peso do objetivo na perspectiva"
          >
            Peso {parseFloat(objective.weight)}
          </span>
        )}
      </div>

      {/* Rodapé: id */}
      <div className="mt-2 text-[10px] text-gray-500 font-medium">#{objective.objectiveId}</div>
    </div>
  );
};

const ObjectiveDetailModal = ({
  objective,
  closeModal,
  getPerformanceColor,
  getPerformanceIcon,
  searchTerm,
}) => {
  const highlightText = (text, term) => {
    if (!term.trim()) return text;
    const regex = new RegExp(`(${term})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 px-1 rounded">
          {part}
        </mark>
      ) : (
        part
      ),
    );
  };

  const formatValue = (value, unit) => {
    if (unit === "R$") {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value);
    }
    return `${value} ${unit}`;
  };

  const getPerspectiveColor = (perspective) => {
    switch (perspective) {
      case "Financeiro":
        return "bg-emerald-600";
      case "Clientes":
        return "bg-blue-600";
      case "Processos":
        return "bg-purple-600";
      case "Pessoas":
        return "bg-orange-600";
      default:
        return "bg-gray-600";
    }
  };

  const getPerspectiveIcon = (perspective) => {
    switch (perspective) {
      case "Financeiro":
        return <DollarSign className="w-5 h-5" />;
      case "Clientes":
        return <Users className="w-5 h-5" />;
      case "Processos":
        return <Settings className="w-5 h-5" />;
      case "Pessoas":
        return <Target className="w-5 h-5" />;
      default:
        return <Target className="w-5 h-5" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header do Modal */}
        <div className={`${getPerspectiveColor(objective.perspective)} text-white p-6 rounded-t-2xl`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {getPerspectiveIcon(objective.perspective)}
              <div>
                <h2 className="text-2xl font-bold">
                  {highlightText(objective.objective, searchTerm)}
                </h2>
                <p className="text-white/80">Objetivo #{objective.objectiveId}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-sm opacity-75">Performance</div>
                <div className="text-3xl font-bold">{objective.average}%</div>
              </div>
              <button
                onClick={closeModal}
                className="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Conteúdo do Modal */}
        <div className="p-6">
          {/* Informações Gerais */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Layers className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-600">Perspectiva</span>
              </div>
              <div className="text-lg font-bold text-gray-900">{objective.perspective}</div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-600">Indicadores</span>
              </div>
              <div className="text-lg font-bold text-gray-900">{objective.indicators.length}</div>
            </div>

            <div className={`rounded-lg p-4 ${getPerformanceColor(objective.average)}`}>
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-white" />
                <span className="text-sm font-medium text-white">Performance Média</span>
              </div>
              <div className="text-lg font-bold text-white">{objective.average}%</div>
            </div>

            <div className="bg-amber-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Scale className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-700">Peso do Objetivo</span>
              </div>
              <div className="text-lg font-bold text-amber-900">
                {objective.weight != null ? parseFloat(objective.weight) : 1}
              </div>
            </div>
          </div>

          {/* Lista Detalhada de Indicadores */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Indicadores de Desempenho
            </h3>

            <div className="space-y-4">
              {objective.indicators.map((indicator) => (
                <div
                  key={indicator.indicatorId}
                  className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 text-lg mb-2">
                        {highlightText(indicator.indicator, searchTerm)}
                      </h4>
                      <div className="text-sm text-gray-600 flex items-center gap-2">
                        <span>ID: {indicator.indicatorId}</span>
                        {indicator.weight != null && (
                          <span
                            className="inline-flex items-center rounded bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 ring-1 ring-amber-200"
                            title="Peso do indicador no objetivo (usado no cálculo da performance)"
                          >
                            Peso {parseFloat(indicator.weight)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold ${getPerformanceColor(indicator.percentAchieved)}`}>
                      {getPerformanceIcon(indicator.percentAchieved)}
                      {indicator.percentAchieved.toFixed(1)}%
                    </div>
                  </div>

                  {/* Métricas */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-blue-700 mb-1">Meta</div>
                      <div className="text-xl font-bold text-blue-900">
                        {formatValue(indicator.target, indicator.unit)}
                      </div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-green-700 mb-1">Realizado</div>
                      <div className="text-xl font-bold text-green-900">
                        {formatValue(indicator.actual, indicator.unit)}
                      </div>
                    </div>
                  </div>

                  {/* Barra de Progresso */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">Progresso</span>
                      <span className="text-sm font-bold text-gray-900">
                        {indicator.percentAchieved.toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all duration-300 ${indicator.percentAchieved >= 100 ? 'bg-green-500' :
                          indicator.percentAchieved >= 90 ? 'bg-blue-500' :
                            indicator.percentAchieved >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                        style={{ width: `${Math.min(indicator.percentAchieved, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Ações Propostas */}
                  {indicator.proposed_actions && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <h5 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4" />
                        Ações Propostas
                      </h5>
                      <p className="text-yellow-900 text-sm">{indicator.proposed_actions}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Conexões */}
          {objective.connections && objective.connections.length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
              <h3 className="font-bold text-purple-800 mb-4 flex items-center gap-2">
                <ArrowRight className="w-5 h-5" />
                Conexões Estratégicas
              </h3>
              <div className="flex flex-wrap gap-2">
                {objective.connections.map((connectionId) => (
                  <span
                    key={connectionId}
                    className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium"
                  >
                    Objetivo #{connectionId}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StrategicFlow;
